import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, Theme, Notification } from '../types';
import { MOCK_USERS, MOCK_NOTIFICATIONS } from '../mock';

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, role?: string) => boolean;
  logout: () => void;

  // Theme
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;

  // Sidebar
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;

  // Notifications
  notifications: Notification[];
  markNotificationRead: (id: string) => void;
  markAllRead: () => void;
  unreadCount: () => number;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      login: (email: string, role?: string) => {
        const found = MOCK_USERS.find(u =>
          role ? u.role === role : u.email.toLowerCase() === email.toLowerCase()
        );
        if (found) {
          set({ user: found, isAuthenticated: true });
          return true;
        }
        // Default admin login for demo
        if (email === 'admin' || email === 'demo') {
          set({ user: MOCK_USERS[0], isAuthenticated: true });
          return true;
        }
        return false;
      },
      logout: () => set({ user: null, isAuthenticated: false }),

      theme: 'light',
      toggleTheme: () => {
        const next = get().theme === 'light' ? 'dark' : 'light';
        set({ theme: next });
        document.documentElement.setAttribute('data-theme', next);
      },
      setTheme: (t: Theme) => {
        set({ theme: t });
        document.documentElement.setAttribute('data-theme', t);
      },

      sidebarOpen: true,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set(s => ({ sidebarOpen: !s.sidebarOpen })),

      notifications: MOCK_NOTIFICATIONS,
      markNotificationRead: (id) =>
        set(s => ({
          notifications: s.notifications.map(n => n.id === id ? { ...n, read: true } : n),
        })),
      markAllRead: () =>
        set(s => ({ notifications: s.notifications.map(n => ({ ...n, read: true })) })),
      unreadCount: () => get().notifications.filter(n => !n.read).length,
    }),
    {
      name: 'cates-roofing-store',
      // Authentication is persisted, but every new app load starts in bright mode.
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
